import { Component, DestroyRef, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import * as AuthSelectors from '../../../store/auth/auth.selectors';
import * as AuthActions from '../../../store/auth/auth.actions';
import * as AppSelectors from '../../../store/app/app.selectors';
import * as AppActions from '../../../store/app/app.actions';
import { AuthSubscriptionsService } from '../../../shared/services/auth-subscriptions/auth-subscriptions.service';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, filter, first, map, switchMap, takeWhile, tap } from 'rxjs/operators';
import { AppState } from 'src/app/store/app/app.state';
import { AuthState, TwoFactorChallenge, TwoFactorEnrolment } from '../../../store/auth/auth.reducer';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LATE_ERROR_STATE } from '../../../shared/helpers/error-state';
import { sha256Hex } from '../../../shared/helpers/sha256';
import { PlatformService } from '../../../shared/services/platform.service';
import { ApiService } from '../../../shared/services/api.service';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { saveTrustedBrowser } from '../../../shared/helpers/trusted-browser';
import { clock, codeDigits } from '../../../shared/helpers/sign-in-wait';
import { TwoFactorEnrolResponse } from '../../../shared/models/twoFactor';
import { TwoFactorSetupDialogComponent, TwoFactorSetupDialogData } from '../../../shared/dialogs/two-factor-setup-dialog/two-factor-setup-dialog.component';

@Component({
    selector: 'urban-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false,
    providers: [LATE_ERROR_STATE]
})
export class LoginComponent implements OnInit, OnDestroy {
  private static readonly SLIDE_INTERVAL_MS = 5000;
  private static readonly SWIPE_THRESHOLD_PX = 60;

  private readonly fb = inject(UntypedFormBuilder);
  private readonly authService = inject(AuthSubscriptionsService);
  private readonly authStore = inject(Store<AuthState>);
  private readonly appStore = inject(Store<AppState>);
  private readonly translate = inject(TranslateService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly apiService = inject(ApiService);
  private readonly loaderService = inject(LoaderService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  // The app keeps its session for 30 days already: no browser to remember.
  public readonly isNative: boolean = inject(PlatformService).isNative;

  public form: UntypedFormGroup = this.fb.group({
    username: ['', Validators.required],
    password: ['', Validators.required],
  });
  public error: string = '';
  // Seconds before the BE accepts another attempt; the button waits with it.
  public lockSeconds: number = 0;
  public lockLabel: string = '';
  public hide: boolean = true;
  public icons: Record<string, SafeHtml> = {};

  public challenge: TwoFactorChallenge = null;
  public codeForm: UntypedFormGroup = this.fb.group({
    code: [''],
    trust: [false]
  });
  public useRecovery: boolean = false;
  // A phrase key, from the store or from the check before sending.
  public codeError: string = null;
  public resendSeconds: number = 0;
  public resendLabel: string = '';

  public slides: string[] = [
    'assets/img/auth/slide-devices.png',
    'assets/img/auth/slide-trashbin.png',
  ];
  public currentSlide: number = 0;
  public isDragging: boolean = false;
  public dragDeltaX: number = 0;
  private dragStartX: number = 0;
  private slideTimer: ReturnType<typeof setInterval> | null = null;

  // Focused as it appears, so the code can be typed or pasted at once.
  @ViewChild('codeInput') set codeInput(input: ElementRef<HTMLInputElement> | undefined) {
    if (input) {
      setTimeout(() => input.nativeElement.focus());
    }
  }

  public get sliderTransform(): string {
    return `translateX(calc(${-this.currentSlide} * (var(--cardW) + var(--cardGap)) + ${this.dragDeltaX}px))`;
  }

  ngOnInit(): void {
    this.startSlider();

    this.appStore.dispatch(AppActions.resetReducers());

    this.authStore.select(AuthSelectors.error).pipe(
      takeUntilDestroyed(this.destroyRef),
      filter(err => !!err),
      switchMap(err =>
        err === 'customError' ? this.translate.get('DEVICE.')
          : err.startsWith('LOGIN.') ? this.translate.get(err) : of(err))
      )
      .subscribe(errorMessage => {
        this.error = errorMessage;
        this.authService.setErrorComand({ error: '' });
    });

    this.authStore.select(AuthSelectors.getLoginLockedUntil).pipe(
      switchMap(until => LoginComponent.countdown(until)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(seconds => {
      this.lockSeconds = seconds;
      this.lockLabel = clock(seconds);

      if (this.lockSeconds > 0) {
        this.error = null;
      }
    });

    const challenge$: Observable<TwoFactorChallenge> = this.authStore.select(AuthSelectors.getChallenge);

    challenge$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(challenge => {
      if (challenge && challenge.ticket !== this.challenge?.ticket) {
        this.codeForm.reset({ code: '', trust: false });
        this.useRecovery = false;
        this.error = null;
      } else if (challenge?.resent && challenge.expiresAt !== this.challenge?.expiresAt) {
        // The code already typed stopped working.
        this.codeForm.get('code').setValue('');
      }

      this.challenge = challenge;
      this.codeError = challenge?.error ?? null;
    });

    challenge$.pipe(
      map(challenge => challenge?.resendAt ?? null),
      distinctUntilChanged(),
      switchMap(until => LoginComponent.countdown(until)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(seconds => {
      this.resendSeconds = seconds;
      this.resendLabel = clock(seconds);
    });

    challenge$.pipe(
      map(challenge => challenge?.expiresAt ?? null),
      distinctUntilChanged(),
      switchMap(expiresAt => expiresAt === null ? EMPTY : timer(new Date(expiresAt))),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.authStore.dispatch(AuthActions.twoFactorCancelled({ error: 'LOGIN.CODE_EXPIRED' })));

    this.authStore.select(AuthSelectors.getEnrolment).pipe(
      filter(Boolean),
      distinctUntilChanged((a, b) => a.ticket === b.ticket),
      switchMap(enrolment => this.enrol(enrolment)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();

    this.appStore.select(AppSelectors.getIcons).pipe(first()).subscribe(response => {
      response.filter(icon => ['visibility-outline', 'visibility-off-outline'].includes(icon.Name))
        .forEach(icon => {
          this.icons[icon.Name] = this.sanitizer.bypassSecurityTrustHtml(icon.Content);
        });
    });
  }

  ngOnDestroy(): void {
    this.stopSlider();
  }

  public goToSlide(index: number): void {
    this.currentSlide = index;
    this.startSlider();
  }

  public onDragStart(event: PointerEvent): void {
    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.stopSlider();
  }

  public onDragMove(event: PointerEvent): void {
    if (this.isDragging) {
      this.dragDeltaX = event.clientX - this.dragStartX;
    }
  }

  public onDragEnd(): void {
    if (!this.isDragging) {
      return;
    }

    if (this.dragDeltaX <= -LoginComponent.SWIPE_THRESHOLD_PX) {
      this.currentSlide = (this.currentSlide + 1) % this.slides.length;
    } else if (this.dragDeltaX >= LoginComponent.SWIPE_THRESHOLD_PX) {
      this.currentSlide = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
    }

    this.isDragging = false;
    this.dragDeltaX = 0;
    this.startSlider();
  }

  private startSlider(): void {
    this.stopSlider();
    this.slideTimer = setInterval(() => {
      this.currentSlide = (this.currentSlide + 1) % this.slides.length;
    }, LoginComponent.SLIDE_INTERVAL_MS);
  }

  private stopSlider(): void {
    if (this.slideTimer !== null) {
      clearInterval(this.slideTimer);
      this.slideTimer = null;
    }
  }

  // Whole seconds left until `until` (epoch ms), every second down to 0.
  private static countdown(until: number | null): Observable<number> {
    return !until ? of(0) : timer(0, 1000).pipe(
      map(() => Math.max(0, Math.ceil((until - Date.now()) / 1000))),
      takeWhile(seconds => seconds > 0, true)
    );
  }

  public async submit(): Promise<void> {
    if (this.lockSeconds > 0) {
      return;
    }

    if (!this.form.valid) {
      this.translate.get('LOGIN.FORM_ERROR').subscribe((error) => {
        this.error = error;
      });

      return;
    }

    const password = await sha256Hex(this.form.get('password').value);
    const bodyLogin = {
      username: this.form.get('username').value,
      password: password
    }

    this.authService.requestLoginComand(bodyLogin);
  }

  public normalizeCode(): void {
    const control = this.codeForm.get('code');
    const digits: string = codeDigits(control.value);

    if (digits !== control.value) {
      control.setValue(digits);
    }

    this.codeError = null;
  }

  public submitCode(): void {
    if (this.lockSeconds > 0) {
      return;
    }

    const code: string = String(this.codeForm.get('code').value ?? '').trim();

    if (this.useRecovery ? code.length === 0 : !/^\d{6}$/.test(code)) {
      this.codeError = 'LOGIN.CODE_FORMAT';
      return;
    }

    this.authStore.dispatch(AuthActions.requestTwoFactorVerify({
      code,
      trust: !this.isNative && this.codeForm.get('trust').value === true,
      recovery: this.useRecovery
    }));
  }

  // The app required past its days: set up here, on the email code just passed,
  // and the session comes with its first code. Leaving it keeps the user out.
  private enrol(enrolment: TwoFactorEnrolment): Observable<unknown> {
    return this.apiService.twoFactorEnrolStart(enrolment.ticket).pipe(
      switchMap(setup => this.dialog.open<TwoFactorSetupDialogComponent, TwoFactorSetupDialogData<TwoFactorEnrolResponse>, TwoFactorEnrolResponse | false>(
        TwoFactorSetupDialogComponent, {
        data: {
          account: enrolment.username,
          setup,
          confirm: code => this.apiService.twoFactorEnrolConfirm(enrolment.ticket, code)
        },
        width: '480px',
        maxWidth: '95vw'
      }).afterClosed()),
      tap(answer => {
        // false: the dialog already said that the setup is over, nothing to add.
        if (answer === false || !answer?.Bearer) {
          this.authStore.dispatch(AuthActions.twoFactorCancelled({ error: answer === false ? null : 'LOGIN.APP_REQUIRED' }));
          return;
        }

        // As after a verify: the browser trusted when asked with the email code.
        if (answer.Trust) {
          void saveTrustedBrowser(enrolment.username, answer.Trust);
        }

        this.loaderService.show();
        this.authStore.dispatch(AuthActions.loginSuccess({ Output: answer.Output, Bearer: answer.Bearer }));
      }),
      catchError(err => {
        this.authStore.dispatch(AuthActions.twoFactorCancelled({
          error: err?.error?.Message === 'Code expired' ? 'LOGIN.CODE_EXPIRED' : 'LOGIN.CODE_UNAVAILABLE'
        }));
        return EMPTY;
      })
    );
  }

  public resend(): void {
    this.authStore.dispatch(AuthActions.requestTwoFactorResend());
  }

  public toggleRecovery(): void {
    this.useRecovery = !this.useRecovery;
    this.codeForm.get('code').setValue('');
    this.codeError = null;
  }

  public backToPassword(): void {
    this.authStore.dispatch(AuthActions.twoFactorCancelled({ error: null }));
  }
}
